"use client";
import { type CSSProperties, type ComponentType, useState, useEffect, useRef } from "react";
import {
  Zap, Shield, DollarSign, Activity, Megaphone, Wifi, ArrowDown,
} from "lucide-react";
import { IsolatedAgentsV2 } from "./IsolatedAgentsV2";
import { NetworkFormation } from "./NetworkFormation";
import { LayeredStackV1 } from "./LayeredStackV1";

/* ─── Graphics ──────────────────────────────────────────────────────── */

const AGENT_DEFS = [
  { label: "Security",  Icon: Shield,    c: "text-red-400",    bg: "bg-red-500/15 border-red-500/25" },
  { label: "Code",      Icon: Zap,       c: "text-blue-400",   bg: "bg-blue-500/15 border-blue-500/25" },
  { label: "Finance",   Icon: DollarSign,c: "text-yellow-400", bg: "bg-yellow-500/15 border-yellow-500/25" },
  { label: "SRE",       Icon: Activity,  c: "text-green-400",  bg: "bg-green-500/15 border-green-500/25" },
  { label: "Marketing", Icon: Megaphone, c: "text-purple-400", bg: "bg-purple-500/15 border-purple-500/25" },
  { label: "Network",   Icon: Wifi,      c: "text-orange-400", bg: "bg-orange-500/15 border-orange-500/25" },
];

/* ─── Outcome data ──────────────────────────────────────────────────── */



function IocDemoEmbed() {
  return null;
}

/* ─── Component ─────────────────────────────────────────────────────── */

/* ── Journey cards data ────────────────────────────────────────────── */

type JourneyCard = {
  seriesLabel?: string;
  eyebrow: string;
  /** Overrides accent for eyebrow only (e.g. brand blue for IoC). */
  eyebrowColor?: string;
  heading: string;
  body: string;
  tagline?: string;
  tags: string[];
  bulletHeader?: string;
  bulletPoints?: string[];
  /** Centered text-only scene (hero-style); no graphic column. */
  textOnly?: boolean;
  Graphic?: ComponentType;
  accent: string;
};

const JOURNEY_CARDS: JourneyCard[] = [
  {
    eyebrow: "Today's Problem",
    eyebrowColor: "#FACC15",
    heading: "Agents can talk,\nbut they can't\nthink together",
    body: "AI agents are powerful. But they often don't reach ideal outcomes when they must cooperate across organizational boundaries.",
    tags: [],
    Graphic: IsolatedAgentsV2,
    accent: "#049FD9",
  },
  {
    eyebrow: "THE COGNITIVE EVOLUTION",
    eyebrowColor: "#FACC15",
    heading: "Three pillars of\nsuperintelligence",
    tagline: "",
    body: "For AI agents to move from vertical to horizontal scale, they require breakthroughs that have parallels to human intelligence and cooperation.",
    bulletHeader: "They need shared and aligned",
    bulletPoints: ["Intent", "Context", "Reasoning"],
    tags: [],
    Graphic: LayeredStackV1,
    accent: "#a78bfa",
  },
  {
    eyebrow: "INTERNET OF COGNITION",
    eyebrowColor: "#FACC15",
    heading: "Accelerated joint\nproblem solving",
    body: "The Internet of Cognition provides an architecture for improved shared context, guardrails, innovation, and efficiency. This will enable net-new emergent value from multi-agent systems.",
    bulletPoints: ["Individual → Collective cognition", "Improving knowledge over time", "Intelligence beyond coordination"],
    tags: [],
    Graphic: NetworkFormation,
    accent: "#22d3ee",
  },
];

/** Target frame ~696×537; scales down on small viewports / short heights. */
const GRAPHIC_FRAME_STYLE: CSSProperties = {
  width: "min(100%, 696px)",
  maxHeight: "min(537px, 72svh)",
};

const STORY_PANEL_ACCENTS = ["#049FD9", ...JOURNEY_CARDS.map((c) => c.accent)] as const;

const EXPLORE_USE_CASES_CTA = {
  label: "The Impact",
  sectionId: "outcome",
} as const;

const HERO_INTRO_COPY =
  "The Internet of Cognition unites distributed AI agents through a unified Cognition Fabric and a shared Cognition Engine, enabling shared intent, shared memory, and shared reasoning.";

export function SimplifiedV2() {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      
      // Calculate how far the container has been scrolled past its pinned start
      // It pins at top: 0
      const scrollDepth = -rect.top;
      const viewportHeight = window.innerHeight;
      
      if (scrollDepth < 0) {
        setActiveIndex(0);
      } else if (scrollDepth >= viewportHeight) {
        setActiveIndex(2);
      } else {
        const progress = scrollDepth / (viewportHeight / 2);
        setActiveIndex(Math.round(progress));
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSlide = (index: number) => {
    if (!containerRef.current) return;
    const absoluteTop = window.scrollY + containerRef.current.getBoundingClientRect().top;
    window.scrollTo({ top: absoluteTop + (index * window.innerHeight), behavior: "smooth" });
  };

  return (
    <div ref={containerRef} className="bg-[#050511] text-white relative w-full h-[200vh]">
      <div className="sticky top-0 h-[100vh] w-full overflow-hidden">
        {/* Pagination dots on the right */}
        <div className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-3">
          {JOURNEY_CARDS.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollToSlide(i)}
              className="w-8 h-8 flex items-center justify-center group focus:outline-none"
              aria-label={`Go to slide ${i + 1}`}
            >
              <span
                className={`w-[3px] h-[3px] rounded-full transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                  activeIndex === i
                    ? "bg-white/45 shadow-[0_0_0_3px_rgba(255,255,255,0.06)] scale-[1.35]"
                    : "bg-white/15 group-hover:bg-white/30"
                }`}
              />
            </button>
          ))}
        </div>

        <div
          className="relative w-full h-full flex flex-col transition-transform duration-700 ease-out"
          style={{ transform: `translateY(-${activeIndex * 100}%)` }}
        >
          {/* Panels staged as distinct scenes (problem to structure to mechanism/payoff) */}
          {JOURNEY_CARDS.map((card, i) => (
            <div
              key={i}
              className="relative box-border w-full h-full shrink-0 flex flex-col justify-start md:justify-center overflow-y-auto overflow-x-hidden pt-8 pb-12 md:py-20 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              {card.textOnly ? (
              <div className="relative z-10 flex h-full min-h-0 w-full flex-col items-center justify-center px-4 py-6 text-center sm:px-6">
                <h2
                  style={{
                    fontSize: "clamp(1.85rem, 4.2vw, 2.75rem)",
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: "-0.03em",
                    color: "rgba(255,255,255,0.94)",
                    maxWidth: "min(36rem, 92%)",
                  }}
                >
                  {card.heading}
                </h2>
                {card.body && (
                  <p
                    style={{
                      marginTop: "clamp(1.25rem, 3vh, 1.75rem)",
                      fontSize: "clamp(0.95rem, 1.9vw, 1.08rem)",
                      fontWeight: 500,
                      color: "rgba(255,255,255,0.48)",
                      lineHeight: 1.65,
                      maxWidth: "min(34rem, 92%)",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {card.body}
                  </p>
                )}
              </div>
            ) : (
            <div
              className={[
                "relative z-10 flex min-h-0 w-full flex-col items-center justify-start md:justify-center gap-6 md:gap-8 px-4 md:px-8 my-auto",
                "md:flex-row md:items-center md:justify-center md:gap-[40px] md:h-full",
              ].join(" ")}
            >
            <div className="w-full shrink-0 md:shrink md:w-[420px] text-left">
              {card.seriesLabel && (
                <p 
                  className="text-[10px] font-semibold tracking-[0.18em] uppercase mb-[14px]"
                  style={{ color: "#049FD9" }}
                >
                  {card.seriesLabel}
                </p>
              )}
              {card.eyebrow && (
                <p 
                  className="text-sm md:text-base font-medium tracking-[0.18em] uppercase mb-[18px]"
                  style={{ color: card.eyebrowColor ?? card.accent }}
                >
                  {card.eyebrow}
                </p>
              )}
              <h2 className={`text-4xl md:text-[48px] font-[350] leading-[1.2] tracking-[-0.02em] whitespace-pre-line ${card.tagline ? 'mb-[12px]' : 'mb-[20px]'}`}>
                {card.heading}
              </h2>
              {card.tagline && (
                <p className="text-[13.5px] text-white/50 leading-[1.55] mb-[20px] max-w-[420px]">
                  {card.tagline}
                </p>
              )}
              {card.body && (
                <p className={
                  i > 0 
                    ? "text-[14px] font-normal text-[#e2e2e2] leading-[1.75] mb-[24px] whitespace-pre-wrap"
                    : "text-base md:text-[18px] font-[350] text-[#e2e2e2] leading-[1.75] mb-[24px] whitespace-pre-wrap"
                }>
                  {card.body}
                </p>
              )}
              {(card.bulletHeader || (card.bulletPoints && card.bulletPoints.length > 0)) && (
                <div style={{ marginBottom: "10px", fontSize: "18px", fontWeight: 350, color: "rgba(255,255,255,0.8)" }}>
                  {card.bulletHeader && <p style={{ marginBottom: "8px" }}>{card.bulletHeader}</p>}
                  {card.bulletPoints && card.bulletPoints.length > 0 && (
                    <ul style={{ listStyleType: "disc", paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "6px" }}>
                      {card.bulletPoints.map((pt, idx) => (
                        <li key={idx} className="pl-1 text-[rgba(255,255,255,0.8)] marker:text-[rgba(255,255,255,0.4)]">{pt}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {card.tags.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {card.tags.map(tag => (
                    <span key={tag} style={{ padding: "4px 12px", borderRadius: "999px", background: `${card.accent}14`, border: `1px solid ${card.accent}28`, fontSize: "11px", color: card.accent, fontWeight: 500 }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              {i === JOURNEY_CARDS.length - 1 && (
                <button
                  className="mt-8 flex items-center justify-start gap-4 text-[13px] md:text-[14px] font-[600] text-[#FACC15] uppercase tracking-widest hover:opacity-80 transition-opacity"
                  onClick={(e) => {
                    e.preventDefault();
                    window.scrollBy({ top: window.innerHeight * 0.85, behavior: "smooth" });
                  }}
                >
                  <ArrowDown className="w-5 h-5" strokeWidth={2} />
                  <span>The Impact</span>
                  <ArrowDown className="w-5 h-5" strokeWidth={2} />
                </button>
              )}
            </div>

            {card.Graphic && (
            <div
              className="min-h-[480px] mx-auto shrink-0 md:shrink overflow-hidden rounded-xl border border-white/[0.14] bg-[#06080f]/60 md:mx-0 md:min-h-0 md:aspect-[696/537]"
              style={GRAPHIC_FRAME_STYLE}
            >
              <div className="flex h-full min-h-0 w-full items-center justify-center [&>*]:max-h-full [&>*]:min-h-0 [&>*]:w-full [&>*]:max-w-full">
                <card.Graphic />
              </div>
            </div>
            )}
            </div>
            )}
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}
